import type { DeedPassed, LegalEntity, Maybe, RegisterMutation } from 'generated/gql';
import {
  AttachmentType,
  MutationType,
  DocumentType,
  useDeleteFileMutation,
  useUpdateRegisterMutation,
  useUpdateLegalEntityMutation,
} from 'generated/gql';
import { Box, Button, Checkbox } from '@rulebooks/evidend-titan';
import { yupResolver } from '@hookform/resolvers/yup';
import { t, Trans } from '@lingui/macro';
import { Column, Columns } from 'components/Columns';
import { DatePickerField } from 'components/FormFields/DatePickerField';
import { InputField } from 'components/FormFields/InputField';
import { SelectField } from 'components/FormFields/SelectField';
import { useWizardContext, WizardFooter, WizardHeader } from 'components/Wizard';
import dayjs from 'dayjs';
import type { DocumentUploadFormValues } from 'forms/document/DocumentUploadForm';
import { DocumentUploadForm, documentUploadFormSchema } from 'forms/document/DocumentUploadForm';
import { dateBasicValidation } from 'forms/helpers/dateTransform';
import { setFormErrors } from 'forms/helpers/setFormErrors';
import {
  LiquidationReasonForm,
  liquidationReasonDefaults,
  liquidationReasonFormSchema,
  toLiquidationReason,
} from 'forms/mutation/LiquidationReasonForm';
import { mapLegalEntityToLegalEntityInput } from 'graphql/input-mappers/LegalEntity';
import { formatDocumentType } from 'helpers/document';
import { formatMutationType, mutationLabelObject } from 'helpers/formatMutation';
import { useFormReset } from 'hooks/useFormReset';
import { useGlobalError } from 'hooks/useGlobalError';
import { useLabels } from 'hooks/useLabels';
import { useUploadDocument } from 'hooks/useUpdateDocument';
import { cloneDeep, isEqual } from 'lodash';
import type { MutationInputFields } from 'mutations/mutationFields';
import { useCallback, useMemo } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';

import { attachmentOptions } from 'wizards/Attachment/generators';
import * as yup from 'yup';

// These types carry no payload of their own, so their wizard has a single data step: the
// effective date is captured here instead of on a screen of its own.
const AOA_MUTATION_TYPES: MutationType[] = [
  MutationType.ArticleOfAssociation,
  MutationType.TrustConditions,
];

// Every type whose wizard has a single data step, so the effective date belongs on this screen.
const EFFECTIVE_DATE_ON_THIS_STEP: MutationType[] = [
  ...AOA_MUTATION_TYPES,
  MutationType.Liquidation,
];

type UploadDocumentFormValues = {
  document: Maybe<DocumentUploadFormValues>;
  incorporatedOn?: Date;
  acknowledgedDate?: Date;
  effectiveDate?: Date;
  attachmentType?: AttachmentType;
  attachedAt?: string | null;
  title?: string | null;
  isUpdateOfAoA?: boolean | null;
  replacement?: boolean | null;
  reasonSelect?: string;
  reason?: string | null;
};

interface CustomMutationFieldsProps {
  mutationType: MutationType;
  shouldAcknowledge: boolean;
}
const CustomMutationFields = ({ mutationType, shouldAcknowledge }: CustomMutationFieldsProps) => {
  const { watch, setValue } = useFormContext();
  const replacement = watch(`replacement`);
  const hasReplacementOption = useMemo(
    () => [MutationType.Incorporation, MutationType.Onboarding].includes(mutationType),
    [mutationType],
  );
  return (
    <>
      {[MutationType.Attachment, MutationType.EndAttachment].includes(mutationType) && (
        <InputField
          label={t`Tijdstip overeenkomst`}
          name="attachedAt"
          placeholder={t`uu:mm`}
          required={mutationType === MutationType.Attachment}
          spaceAfter={6}
        />
      )}
      {mutationType === MutationType.Attachment && (
        <SelectField
          required
          defaultValue={AttachmentType.Prejudgement}
          label={t`Beslagtype`}
          name="attachmentType"
          options={attachmentOptions()}
          placeholder={t`Kies een type`}
        />
      )}

      {shouldAcknowledge && (
        <DatePickerField
          isFutureDisabled
          label={t`Datum van erkenning/betekening`}
          name="acknowledgedDate"
          placeholder={t`Datum van erkenning/betekening`}
        />
      )}

      {hasReplacementOption && (
        <Box spaceAfter={6}>
          <Checkbox
            isChecked={!!replacement}
            label={t`Vervangend register`}
            name=""
            onChange={(value) => setValue(`replacement`, value)}
          />
        </Box>
      )}
    </>
  );
};

const defaultDocumentFormValues = (
  mutation: RegisterMutation,
  effectiveDate: Date | undefined,
  canChooseType: boolean,
  isFoundation: boolean,
  isNoDocumentAllowed?: boolean,
) => {
  if (!mutation.document && !isNoDocumentAllowed) {
    const defaultDocument = {
      type: undefined as DocumentType | undefined,
      labels: mutationLabelObject[mutation.type].defaultLabels(isFoundation),
      date: effectiveDate,
      deedPassed: undefined as DeedPassed | undefined,
      dossierNumber: mutation.dossierNumber,
    };
    if (!canChooseType) defaultDocument.type = DocumentType.NotarialDeed;
    if (mutationLabelObject[mutation.type].defaultDocumentType)
      defaultDocument.type = mutationLabelObject[mutation.type].defaultDocumentType;
    return defaultDocument;
  }
  return mutation.document
    ? { ...mutation.document, dossierNumber: mutation.dossierNumber }
    : mutation.document;
};

const defaultValues = (
  mutation: RegisterMutation,
  legalEntity: LegalEntity,
  canChooseType: boolean,
  isNoDocumentAllowed = false,
) => {
  const { type: mutationType, acknowledgedDate, title } = mutation;
  const defaultEffectiveDate = () => {
    // A register is virtually always ended on the day it is registered.
    if (mutationType === MutationType.Liquidation) return new Date();
    // Falling back to the incorporation date would silently pre-fill a valid but wrong date for
    // the AoA types, where the date is entered on this step; they start out empty instead.
    if (AOA_MUTATION_TYPES.includes(mutationType)) return undefined;
    return new Date(mutation.register.legalEntity.incorporatedOn);
  };
  const effectiveDate = mutation.effectiveDate || defaultEffectiveDate();
  const values: UploadDocumentFormValues = {
    document: defaultDocumentFormValues(
      mutation,
      effectiveDate,
      canChooseType,
      legalEntity.isFoundation,
      isNoDocumentAllowed,
    ) as DocumentUploadFormValues,
    incorporatedOn: legalEntity.incorporatedOn,
    acknowledgedDate,
    title,
    effectiveDate,
    replacement: mutation.register.replacement,
  };
  if (mutationType === MutationType.Liquidation)
    Object.assign(values, liquidationReasonDefaults(mutation.liquidation?.reason));
  if ([MutationType.Attachment, MutationType.EndAttachment].includes(mutationType)) {
    const mutationField = mutationLabelObject[mutationType]
      .field as MutationInputFields['ATTACHMENT'];
    values.attachmentType = mutation[mutationField]?.[0]?.type;
    const defaultAttachedAt =
      mutationType === MutationType.Attachment ? dayjs().format('HH:mm') : null;
    values.attachedAt = mutation[mutationField]?.[0]?.details?.attachedAt || defaultAttachedAt;
  }
  return values;
};

const getFormSchema = (mutationType: MutationType) => {
  const formSchema = yup.object({
    title: yup.string().trim().nullable(),
    acknowledgedDate: dateBasicValidation,
    document: documentUploadFormSchema.nullable(),
  });
  if (mutationType === MutationType.Onboarding)
    return formSchema.concat(
      yup.object({
        incorporatedOn: dateBasicValidation.required(),
      }),
    );
  if (mutationType === MutationType.Liquidation)
    return formSchema
      .concat(
        yup.object({
          effectiveDate: dateBasicValidation.required(),
        }),
      )
      .concat(liquidationReasonFormSchema);
  if (
    mutationType === MutationType.Incorporation ||
    EFFECTIVE_DATE_ON_THIS_STEP.includes(mutationType)
  )
    return formSchema.concat(
      yup.object({
        effectiveDate: dateBasicValidation.required(),
      }),
    );
  if ([MutationType.Attachment, MutationType.EndAttachment].includes(mutationType)) {
    const attachedAt = yup
      .string()
      .trim()
      .nullable()
      .matches(
        /^(2[0-3]|[01]?[0-9]):([0-5]?[0-9])$/,
        t`De tijd moet in het volgende formaat worden ingevoerd: "23:59"`,
      )
      .length(5);
    if (mutationType === MutationType.EndAttachment)
      return formSchema.concat(
        yup.object({
          attachedAt,
        }),
      );
    return formSchema.concat(
      yup.object({
        attachedAt: attachedAt.required(),
        attachmentType: yup.string().oneOf(Object.values(AttachmentType)).required(),
      }),
    );
  }

  return formSchema;
};

export const UploadDocument = () => {
  const { previousStep, nextStep, visitStep, mutation, saveMutation } = useWizardContext();
  const { type: mutationType, register } = mutation;
  const { legalEntity } = register;
  const labels = useLabels('uploadDocument', legalEntity.isFoundation);
  const uploadDocument = useUploadDocument();
  const [deleteFile] = useDeleteFileMutation();
  const [updateRegister] = useUpdateRegisterMutation();
  const [, setGlobalError] = useGlobalError();
  const [updateLegalEntityQuery] = useUpdateLegalEntityMutation();

  const canChooseType = mutationLabelObject[mutationType].hasDocumentChoiceStep(
    legalEntity.isFoundation,
  );
  const isNoDocumentAllowed = !!mutationLabelObject[mutationType].isNoDocumentAllowed;
  const formSchema = useMemo(() => getFormSchema(mutationType), [mutationType]);
  const form = useForm<UploadDocumentFormValues>({
    defaultValues: defaultValues(mutation, legalEntity, canChooseType, isNoDocumentAllowed),
    resolver: yupResolver(formSchema),
    reValidateMode: 'onChange',
    mode: 'onChange',
  });
  const reset = useFormReset(form);
  const { getValues, handleSubmit, setValue } = form;
  const { isSubmitting, isValid } = form.formState;
  const isSubmitDisabled = !isValid || isSubmitting;
  const documentType = form.watch('document.type');
  const document = form.watch('document');
  const shouldAcknowledge = mutationLabelObject[mutationType].shouldAcknowledge;

  const setRegister = useCallback(
    async ({ replacement }: UploadDocumentFormValues) => {
      await updateRegister({
        variables: {
          registerId: register.id,
          register: {
            replacement: !!replacement,
          },
        },
        onError: ({ graphQLErrors }) => {
          setFormErrors({ errors: graphQLErrors, form, setGlobalError });
        },
      });
    },
    [form, register.id, setGlobalError, updateRegister],
  );

  const setIncorporatedOn = useCallback(
    async (formValues: UploadDocumentFormValues) => {
      const incorporatedOn =
        mutationType === MutationType.Incorporation
          ? formValues.effectiveDate
          : formValues.incorporatedOn;

      // We only want to change incorporatedOn if it has changed from original value
      if (incorporatedOn && !isEqual(incorporatedOn, legalEntity.incorporatedOn))
        await updateLegalEntityQuery({
          variables: {
            legalEntityId: legalEntity.id,
            legalEntity: Object.assign({}, mapLegalEntityToLegalEntityInput(legalEntity), {
              incorporatedOn,
            }),
          },
          onError: ({ graphQLErrors }) => {
            setFormErrors({ errors: graphQLErrors, form, setGlobalError });
          },
        });
    },
    [form, legalEntity, mutationType, setGlobalError, updateLegalEntityQuery],
  );

  const onSubmit = useCallback(async () => {
    const formValues = getValues();
    const {
      document: documentValues,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      incorporatedOn: _incorporatedOn,
      attachmentType,
      attachedAt,
      // Folded back into `liquidation.reason` below, so they must not leak into the mutation.
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      reasonSelect: _reasonSelect,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      reason: _reason,
      ...mutationValues
    } = formValues;
    const { dossierNumber, ...documentValuesWithoutDossier } = documentValues ?? {};
    const document = documentValues
      ? {
          ...mutation.document,
          ...documentValuesWithoutDossier,
        }
      : null;

    if ([MutationType.Incorporation, MutationType.Onboarding].includes(mutationType)) {
      await Promise.all([setRegister(formValues), setIncorporatedOn(formValues)]);
    }

    const toSaveMutation = cloneDeep({
      ...mutation,
      document: document as any,
      ...mutationValues,
      dossierNumber: dossierNumber ?? null,
    });

    if (mutationType === MutationType.Liquidation)
      toSaveMutation.liquidation = { reason: toLiquidationReason(formValues) };

    if ([MutationType.Attachment, MutationType.EndAttachment].includes(mutationType)) {
      const mutationField = mutationLabelObject[mutationType]
        .field as MutationInputFields['ATTACHMENT'];
      toSaveMutation[mutationField]?.forEach((attachment) => {
        if (attachmentType) attachment.type = attachmentType;
        if (attachedAt && attachment.details) attachment.details.attachedAt = attachedAt;
      });
    }

    if (mutationType === MutationType.Onboarding)
      toSaveMutation.effectiveDate = documentValues?.date;

    await saveMutation({
      mutation: toSaveMutation,
      onCompleted: async ({ updateMutation }) => {
        if (updateMutation.document && formValues.document?.file?.content) {
          await uploadDocument({
            document: updateMutation.document,
            file: formValues.document.file,
            onDocumentSave: async () => {
              await reset(formValues);
              visitStep(nextStep);
            },
          });
        } else if (updateMutation.document?.file && !formValues.document?.file) {
          await deleteFile({
            variables: {
              documentId: updateMutation.document.id,
            },
            onCompleted: async () => {
              await reset(formValues);
              visitStep(nextStep);
            },
          });
        } else {
          await reset(formValues);
          visitStep(nextStep);
        }
      },
      onError: async ({ graphQLErrors }) => {
        setFormErrors({
          errors: graphQLErrors,
          form,
          setGlobalError,
        });
      },
    });
  }, [
    deleteFile,
    form,
    getValues,
    mutation,
    mutationType,
    nextStep,
    reset,
    saveMutation,
    setGlobalError,
    setIncorporatedOn,
    setRegister,
    uploadDocument,
    visitStep,
  ]);

  const canChooseDate = ![MutationType.Attachment, MutationType.EndAttachment].includes(
    mutationType,
  );
  const isOnboarding = mutationType === MutationType.Onboarding;
  const headerDocumentLabel = isOnboarding
    ? labels.uploadDocumentOnboarding
    : labels.uploadDocument;
  const subHeaderDocumentLabel = documentType ? formatDocumentType(documentType, mutationType) : '';
  const defaultMutationTypeName = formatMutationType({ type: mutationType });

  return (
    <FormProvider {...form}>
      <WizardHeader previousStep={previousStep} title={headerDocumentLabel}>
        <Trans>Voeg de {subHeaderDocumentLabel} toe en de bijbehorende gegevens.</Trans>
      </WizardHeader>

      <Columns count={2}>
        <Column>
          {mutationType === MutationType.Onboarding && (
            <DatePickerField
              isFutureDisabled
              required
              label={t`Oprichtingsdatum`}
              name="incorporatedOn"
              placeholder={t`Oprichtingsdatum`}
              spaceAfter={6}
            />
          )}

          <InputField
            label={t`Titel rechtsfeit`}
            name="title"
            placeholder={defaultMutationTypeName}
            spaceAfter={6}
          />

          {isNoDocumentAllowed && (
            <Box spaceAfter={6}>
              <Checkbox
                isChecked={document === null}
                label={t`Geen document toevoegen`}
                name=""
                onChange={(value) => {
                  if (value)
                    setValue('document', null, { shouldValidate: true, shouldDirty: true });
                  else {
                    const { document } = defaultValues(mutation, legalEntity, canChooseType);
                    setValue('document', document, { shouldValidate: true, shouldDirty: true });
                  }
                }}
              />
            </Box>
          )}

          {(mutationType === MutationType.Incorporation ||
            EFFECTIVE_DATE_ON_THIS_STEP.includes(mutationType) ||
            !document) && (
            <DatePickerField
              required
              isFutureDisabled={AOA_MUTATION_TYPES.includes(mutationType)}
              label={t`Datum inwerkingtreding`}
              name="effectiveDate"
              placeholder={t`Datum inwerkingtreding`}
              spaceAfter={6}
            />
          )}

          {mutationType === MutationType.Liquidation && <LiquidationReasonForm />}

          {!document && (
            <CustomMutationFields
              mutationType={mutationType}
              shouldAcknowledge={shouldAcknowledge}
            />
          )}
        </Column>
      </Columns>

      {document && (
        <DocumentUploadForm
          canChooseDate={canChooseDate}
          canChooseType={canChooseType}
          cardBackgroundVariant="white"
          isFoundation={legalEntity.isFoundation}
          mutationType={mutationType}
          nestedField="document."
        >
          <CustomMutationFields mutationType={mutationType} shouldAcknowledge={shouldAcknowledge} />
        </DocumentUploadForm>
      )}

      <WizardFooter
        isLoading={isSubmitting}
        label={t`Sla de gegevens van het document op en ga verder om alle gegevens nog eens te controleren.`}
      >
        <Button isDisabled={isSubmitDisabled} variant="primary" onClick={handleSubmit(onSubmit)}>
          <Trans>Volgende</Trans>
        </Button>
      </WizardFooter>
    </FormProvider>
  );
};
